import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { AppState, ConfigService, isDeepEqual, mapErrorForSentry, SentryLogger } from '@fcom/core';
import {
  CartOrOrder,
  CommonBookingState,
  ServicesActions,
  servicesReady,
  TRAIN_AIRCRAFT_CODE,
} from '@fcom/common-booking';
import {
  ButtonMode,
  ButtonPosition,
  ButtonSize,
  ButtonTheme,
  LoaderTheme,
  ModalButtons,
  NotificationTheme,
} from '@fcom/ui-components';
import { ToasterService } from '@fcom/common/services';
import { LanguageService } from '@fcom/ui-translate';
import { GtmService } from '@fcom/common/gtm';
import {
  ElementActions,
  ElementTypes,
  GaContext,
  GtmEvent,
  GtmPurchaseFlow,
  TestVariantWithId,
  TripType,
} from '@fcom/common/interfaces';
import {
  Amount,
  BoundType,
  CartStatus,
  CustomServiceType,
  LocationUtil,
  ServiceCatalogCategory,
  TravelerService,
} from '@fcom/dapi';
import {
  Category,
  ControlData,
  FareFamily,
  FareRuleCategory,
  FinnairBoundItem,
  FinnairCart,
  FinnairFrequentFlyerCardItem,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPicture,
  FinnairServiceItem,
  ServiceFlow,
  TranslatedBenefitRow,
} from '@fcom/dapi/api/models';
import { finShare, safeMap, safeMap as safeMapObs, snapshot } from '@fcom/rx';
import { getBookingDestinationSummary } from '@fcom/common/utils/booking.utils';
import {
  additionalEmailValidator,
  getAllFlightsFromCartOrOrder,
  getTripTypeFromBounds,
  hasBusSegment,
  isAwardBooking,
  isFlexFareFamilyBound,
  isFlight,
  isUsingSAF,
  toAmount,
} from '@fcom/common-booking/utils/common-booking.utils';
import { Step } from '@fcom/common';
import { changedServices, getServicesToShow } from '@fcom/common-booking/modules/ancillaries/utils';
import { containsLongHaulFlight, getGA4Items, resolvePurchaseFlow } from '@fcom/common/utils/gtm.utils';
import { profileOrNot, profileType } from '@fcom/core/selectors';
import { EMAIL_PATTERN, isPresent, unsubscribe, valuesOf } from '@fcom/core/utils';
import {
  serviceMapForCategories,
  serviceSelectionsForFragment,
  servicesForCategory,
  servicesForFragmentAndCategory,
} from '@fcom/common-booking/store/selectors/services.selector';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { BookingSteps } from '@fcom/common/interfaces/booking/booking-step.interface';
import { PaxDetailsState } from '@fcom/common/interfaces/booking';
import { ProfileType } from '@fcom/core-api/login';
import { hasBound24HourStopOver } from '@fcom/common-booking/utils/bound.utils';

import {
  PaxServiceCategoryPerFragmentWithSelections,
  ServiceCatalogServiceWithSelection,
  ServiceSelectionChange,
} from '../../../pax-details/interfaces/pax-ancillaries.interface';
import { CountryCode } from '../../../../utils';
import { BookingQueueService } from '../../../../services/booking-queue.service';
import { additionalEmails, paxDetails } from '../../../../store/selectors';
import { PaxDetailsActions } from '../../../../store/actions';
import { BookingService } from '../../../../services';
import { GrandTotalTheme } from '../../../common/components/grand-total/grand-total.component';

export const mapRebookPolicy = (fareFamily: FareFamily): TranslatedBenefitRow =>
  fareFamily?.translatedBenefitRows.find((row) => row.key === 'rebook');

export const mapCancellationPolicy = (fareFamily: FareFamily): TranslatedBenefitRow =>
  fareFamily?.translatedBenefitRows.find((row) => row.key === 'cancel');

@Component({
  selector: 'fin-purchase-review',
  styleUrls: ['./purchase-review.component.scss'],
  templateUrl: './purchase-review.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseReviewComponent implements OnInit, OnDestroy {
  readonly BoundType = BoundType;
  readonly ModalButtons = ModalButtons;
  readonly FinnairPassengerCode = FinnairPassengerCode;
  readonly ButtonPosition = ButtonPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly ServiceFlow = ServiceFlow;
  readonly GrandTotalTheme = GrandTotalTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly ProfileType = ProfileType;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  STEPS: BookingSteps = BOOKING_STEPS;
  readonly IconLibrary = IconLibrary;

  @Input()
  cartOrOrder$: Observable<CartOrOrder>;

  @Input()
  cartOrOrderStatus$: Observable<CartStatus>;

  @Output()
  ready: EventEmitter<boolean> = new EventEmitter();
  @Output()
  edit: EventEmitter<{ next: Step; openGlobalBookingWidget?: boolean }> = new EventEmitter<{
    next: Step;
    openGlobalBookingWidget?: boolean;
  }>();

  readonly FareRuleCategory = FareRuleCategory;

  isTicketRulesModalOpen = false;
  isTimelineModalOpen = false;
  timelineBoundIndex: number;
  flightCardExpandersOpen: boolean[];

  cartOrOrderStatusUpdating$: Observable<boolean>;
  safContributionUrl$: Observable<string>;
  hasServices$: Observable<boolean>;
  isOneWay$: Observable<boolean>;
  originInChinaOrKorea$: Observable<boolean>;
  showKoreaNotification$: Observable<boolean>;
  image$: Observable<FinnairPicture>;
  summary$: Observable<LocationUtil[]>;
  isOrder$: Observable<boolean>;
  totalPrice$: Observable<Amount>;
  totalPoints$: Observable<number>;
  serviceFlow$: Observable<ServiceFlow | undefined>;
  shouldDisplayCombinedTripMessage$: Observable<boolean>;
  shouldDisplaySafContributionMessage$: Observable<boolean>;
  services$: Observable<FinnairServiceItem[]>;
  testVariant$: Observable<TestVariantWithId>;

  cancellationPolicies$: Observable<TranslatedBenefitRow[]>;
  rebookPolicies$: Observable<TranslatedBenefitRow[]>;

  subscriptions: Subscription = new Subscription();

  showSustainableMessages$: Observable<boolean>;

  servicesReady$: Observable<boolean>;
  isCoverSelected$: Observable<boolean>;
  coverServicesWithSelection$: Observable<PaxServiceCategoryPerFragmentWithSelections>;
  catalogCategory$: Observable<ServiceCatalogCategory>;
  coverIsLoading$ = new BehaviorSubject<boolean>(false);

  scsWithSelection: ServiceCatalogServiceWithSelection;
  travelerId: string;
  enableCancellationCoverUpsell$: Observable<boolean>;

  paxDetails$: Observable<PaxDetailsState> = this.store$.pipe(paxDetails());
  paxAdditionalEmails$: Observable<string[]> = this.store$.pipe(take(1), additionalEmails());
  paxForm = this.formBuilder.group({
    additionalEmail: this.formBuilder.control(
      '',
      [Validators.pattern(EMAIL_PATTERN)],
      [additionalEmailValidator(this.paxAdditionalEmails$)]
    ),
  });
  emailsArray: string[] = [];
  enableAdditionalEmailInBookingFlow = false;
  activeBoundInModal$: BehaviorSubject<FinnairBoundItem> = new BehaviorSubject(null);
  controlData$: Observable<ControlData>;
  profileType$: Observable<ProfileType>;

  allFrequentFlyerCards$: Observable<FinnairFrequentFlyerCardItem[]>;
  enableShowingSAF: boolean;
  enableSafContributionCard$: Observable<boolean>;
  multiCityStopoverDurationNotification$: Observable<boolean>;
  isChangingStep = false;
  enableEditPaxDetailsOnReviewPage: boolean;
  hasTrainSegment$: Observable<boolean>;

  cartBeforeCancellationCoverUpdated$: Subject<CartOrOrder> = new Subject<CartOrOrder>();

  inboundShowFlexTicketNotification$: Observable<string> = of(undefined);

  constructor(
    private languageService: LanguageService,
    private gtmService: GtmService,
    private store$: Store<AppState & CommonBookingState>,
    private bookingQueueService: BookingQueueService,
    private bookingService: BookingService,
    private toasterService: ToasterService,
    private configService: ConfigService,
    private sentryLogger: SentryLogger,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.multiCityStopoverDurationNotification$ = this.cartOrOrder$.pipe(
      distinctUntilChanged(isDeepEqual),
      filter((cartOrOrder) => Boolean(cartOrOrder) && getTripTypeFromBounds(cartOrOrder.bounds) === TripType.MULTICITY),
      map((cartOrOrder) => hasBound24HourStopOver(cartOrOrder.bounds))
    );

    this.enableEditPaxDetailsOnReviewPage = this.configService.cfg.enableEditPaxDetailsOnReviewPage;
    this.subscriptions.add(
      combineLatest([
        this.cartOrOrder$.pipe(
          take(1),
          filter((cart) => !!cart)
        ),
        this.store$.pipe(profileOrNot()),
      ]).subscribe(([cartOrOrder, profile]) => {
        this.gtmService.ecommerceEventGA4(
          GtmEvent.VIEW_CART,
          getGA4Items(cartOrOrder, resolvePurchaseFlow(profile, isAwardBooking(cartOrOrder?.prices?.unpaid)), true)
        );
      })
    );

    this.handleCover();
    this.subscriptions.add(
      this.paxDetails$.subscribe((paxState: PaxDetailsState) => {
        this.emailsArray = paxState?.additionalEmails || this.emailsArray;

        if (this.emailsArray.length >= 3) {
          this.paxForm.get('additionalEmail').disable();
        } else {
          this.paxForm.get('additionalEmail').enable();
        }
      })
    );

    this.cancellationPolicies$ = this.cartOrOrder$.pipe(
      map((cartOrOrder) => {
        const ffCodes = cartOrOrder?.bounds?.map((bound) => bound.fareFamily.code);

        return ffCodes.map((ffCode: string) => mapCancellationPolicy(cartOrOrder?.benefits[ffCode])).filter(isPresent);
      })
    );

    this.allFrequentFlyerCards$ = this.cartOrOrder$.pipe(
      map(({ passengers }) => passengers.flatMap((pax) => pax.frequentFlyerCards ?? []))
    );

    this.rebookPolicies$ = this.cartOrOrder$.pipe(
      map((cartOrOrder) => {
        const ffCodes = cartOrOrder?.bounds?.map((bound) => bound.fareFamily.code);

        return ffCodes.map((ffCode: string) => mapRebookPolicy(cartOrOrder?.benefits[ffCode])).filter(isPresent);
      })
    );

    this.controlData$ = this.cartOrOrder$.pipe(map((order) => order.controlData));
    this.profileType$ = this.store$.pipe(profileType());
    this.totalPrice$ = this.cartOrOrder$.pipe(
      map((data) => data?.prices),
      safeMapObs((prices) => {
        return toAmount(prices.unpaid.total.total.totalAmount);
      })
    );

    this.totalPoints$ = this.cartOrOrder$.pipe(
      filter((data) => !!data?.prices?.unpaid?.total?.total?.totalPoints),
      map((data) => +data.prices.unpaid.total.total.totalPoints.amount)
    );

    this.serviceFlow$ = this.totalPoints$.pipe(
      map((points) => (points ? ServiceFlow.AWARD : undefined)),
      startWith(undefined),
      finShare()
    );

    this.isOrder$ = this.cartOrOrder$.pipe(
      map((d) => d.id.length === 6),
      finShare()
    );

    this.cartOrOrderStatusUpdating$ = this.cartOrOrderStatus$.pipe(
      map((status: CartStatus) => status !== CartStatus.OK),
      startWith(false)
    );

    this.summary$ = this.cartOrOrder$.pipe(map((cd) => getBookingDestinationSummary(cd)));

    this.image$ = combineLatest([this.cartOrOrder$, this.summary$]).pipe(
      map(([cartData, summary]) => cartData.locations[summary[1].code]?.picture),
      filter<FinnairPicture>(Boolean)
    );

    this.shouldDisplayCombinedTripMessage$ = this.cartOrOrder$.pipe(map(hasBusSegment));

    this.shouldDisplaySafContributionMessage$ = this.cartOrOrder$.pipe(
      map((summary) => summary?.bounds?.some((bound) => isUsingSAF(bound) && this.enableShowingSAF))
    );

    this.enableShowingSAF = this.configService.cfg.enableShowingSAF;

    this.enableSafContributionCard$ = combineLatest([
      this.cartOrOrder$,
      this.store$.pipe(servicesForCategory(Category.SAF)),
    ]).pipe(
      map(([cartOrOrder, safAncillary]) => {
        if (cartOrOrder.otherInformation.corporateUserInformation) {
          return false;
        }
        if (!this.configService.cfg.enableSafContributionCard || !safAncillary) {
          return false;
        }
        const safEligibility = cartOrOrder.eligibilities.serviceCatalog?.categories.find((eligibility) => {
          return eligibility.category === Category.SAF;
        });
        return cartOrOrder.bounds.every((bound) => {
          const boundEligibility = safEligibility?.services[bound.id];

          return cartOrOrder.passengers.every((pax) => boundEligibility[pax.id]?.isAllowedToUse);
        });
      }),
      startWith(false)
    );

    this.safContributionUrl$ = this.languageService
      .translate('fragments.safContribution.url')
      .pipe(distinctUntilChanged());

    this.hasServices$ = this.cartOrOrder$.pipe(safeMap((c) => c.services.unpaid.length > 0));

    this.isOneWay$ = this.cartOrOrder$.pipe(safeMap((c) => c.bounds.length === 1));

    const originCountryCode$ = this.cartOrOrder$.pipe(
      filter((cartData) => !!cartData),
      map(
        (cartData) =>
          cartData.locations[(cartData.bounds[0].itinerary[0] as FinnairItineraryItemFlight).departure.locationCode]
            .countryCode
      )
    );

    this.originInChinaOrKorea$ = originCountryCode$.pipe(
      map((countryCode: CountryCode) => [CountryCode.KOREA, CountryCode.CHINA].includes(countryCode)),
      finShare()
    );

    this.showKoreaNotification$ = originCountryCode$.pipe(
      map((countryCode: CountryCode) => CountryCode.KOREA === countryCode),
      finShare()
    );

    this.showSustainableMessages$ = combineLatest([
      this.shouldDisplayCombinedTripMessage$,
      this.shouldDisplaySafContributionMessage$,
      this.safContributionUrl$,
    ]).pipe(
      map(
        ([shouldDisplayCombinedTripMessage, shouldDisplaySafContributionMessage, safContributionUrl]) =>
          shouldDisplayCombinedTripMessage || (shouldDisplaySafContributionMessage && !!safContributionUrl)
      )
    );

    this.enableAdditionalEmailInBookingFlow = this.configService.cfg.enableAdditionalEmailInBookingFlow;

    this.services$ = this.cartOrOrder$.pipe(
      map((cartOrOrder) => getServicesToShow(cartOrOrder?.services, 'unpaid', false, true)),
      finShare()
    );

    this.subscriptions.add(
      this.cartOrOrder$.subscribe((cartOrOrder: FinnairCart | FinnairOrder) => {
        this.flightCardExpandersOpen = new Array(cartOrOrder?.bounds?.length).fill(false);
      })
    );

    this.subscriptions.add(
      combineLatest([this.cartOrOrder$, this.cartBeforeCancellationCoverUpdated$])
        .pipe(
          map(([order, oldOrder]) => changedServices(oldOrder, order, Category.COVER, GtmPurchaseFlow.BOOKING)),
          distinctUntilChanged(isDeepEqual),
          filter(({ addedProducts, removedProducts }) => addedProducts.length > 0 || removedProducts.length > 0)
        )
        .subscribe(({ addedProducts, removedProducts }) => {
          if (addedProducts.length) {
            this.gtmService.ecommerceEventGA4(GtmEvent.ADD_TO_CART, addedProducts);
          }

          if (removedProducts.length) {
            this.gtmService.ecommerceEventGA4(GtmEvent.REMOVE_FROM_CART, removedProducts);
          }
        })
    );

    // Show notification only for long haul flights with flex ticket
    // and only for the inbound flight when there are two bounds (return flight or open-jaw)
    this.inboundShowFlexTicketNotification$ = this.cartOrOrder$.pipe(
      filter((cartOrOrder) => Boolean(cartOrOrder) && cartOrOrder.bounds.length === 2),
      map(({ bounds }) => {
        const inboundFlight = bounds[1];
        return inboundFlight && isFlexFareFamilyBound(inboundFlight) && containsLongHaulFlight([inboundFlight])
          ? inboundFlight.id
          : undefined;
      })
    );

    this.hasTrainSegment$ = this.cartOrOrder$.pipe(
      map((cartOrOrder) => {
        return cartOrOrder.bounds.some((bound) => {
          return bound.itinerary.filter(isFlight).some((i) => {
            return i.aircraft?.code?.toUpperCase() === TRAIN_AIRCRAFT_CODE;
          });
        });
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private handleCover(): void {
    this.servicesReady$ = this.store$.pipe(servicesReady()).pipe(finShare());

    this.catalogCategory$ = this.store$.pipe(
      serviceMapForCategories([Category.COVER]),
      map((array) => array[0])
    );

    this.subscriptions.add(this.catalogCategory$.subscribe());

    const selections$ = this.store$.pipe(
      serviceSelectionsForFragment<TravelerService[]>(CustomServiceType.JOURNEY, Category.COVER),
      map((selections) =>
        valuesOf(selections)
          .reduce((all, services) => all.concat(...services), [])
          .filter((service) => service.quantity > 0)
      ),
      finShare()
    );

    this.isCoverSelected$ = selections$.pipe(
      map((services) => {
        const selectedCoverAsArray = services.map((service) => service.variant === 'CACR');

        if (selectedCoverAsArray.length === 1) {
          return selectedCoverAsArray[0];
        }

        return false;
      })
    );

    this.subscriptions.add(this.isCoverSelected$.subscribe());

    this.coverServicesWithSelection$ = combineLatest([
      this.isCoverSelected$,
      this.store$.pipe(servicesForFragmentAndCategory(CustomServiceType.JOURNEY, Category.COVER)),
    ]).pipe(
      map(([isCoverSelected, coverServices]) => {
        this.travelerId = Object.keys(coverServices)?.[0];
        const serviceCatalogServices = Object.values(coverServices)?.[0];
        const selectedQuantity = isCoverSelected ? 1 : 0;

        const selection = { selected: isCoverSelected, selectedQuantity };

        const serviceCatalogServiceWithSelection = serviceCatalogServices?.map((scs) => ({ ...selection, ...scs }));
        if (!isPresent(serviceCatalogServiceWithSelection)) {
          return null;
        }

        this.scsWithSelection = serviceCatalogServiceWithSelection[0];
        return { [this.travelerId]: serviceCatalogServiceWithSelection };
      })
    );

    this.subscriptions.add(this.coverServicesWithSelection$.subscribe());

    this.subscriptions.add(
      this.servicesReady$.subscribe((serviceReady) => {
        if (serviceReady) {
          this.coverIsLoading$.next(false);
        }
      })
    );
  }

  updateSelectionsForFragment({ category, selection }: ServiceSelectionChange): void {
    this.subscriptions.add(
      combineLatest([this.cartOrOrder$, this.isCoverSelected$])
        .pipe(take(1))
        .subscribe(([currentCartData, isCoverSelected]) => {
          // Checking if the selection is the same as in the store to prevent 2 store updates as the toggle fires twice
          if (isPresent(selection) === isCoverSelected) {
            return;
          }

          try {
            this.store$.dispatch(
              ServicesActions.setCoverForId({
                journeyId: CustomServiceType.JOURNEY,
                travelerId: CustomServiceType.GROUP,
                service: {
                  category: this.scsWithSelection.subCategory,
                  variant: 'CACR',
                  displayName: this.scsWithSelection.translations.title,
                  pricePerItem: this.scsWithSelection.totalPrice,
                  quantity: isCoverSelected ? 0 : 1,
                  includedInTicketType: false,
                  includedInTierBenefit: false,
                },
              })
            );
          } catch (error) {
            this.sentryLogger.error('Selecting cancellation cover failed', {
              error: mapErrorForSentry(error),
            });
          }

          this.coverIsLoading$.next(true);

          const flights: FinnairItineraryItemFlight[] = getAllFlightsFromCartOrOrder(currentCartData);
          const [requests, messages] = getServiceData(this.store$, category, currentCartData.bounds, flights);

          this.bookingQueueService.queueServicesUpdate(requests, {
            error: () => {
              this.toasterService.addMessageToQueue(messages.error);
            },
            complete: () => {
              this.cartBeforeCancellationCoverUpdated$.next(currentCartData);
            },
          });
        })
    );
  }

  backToDateSelection(): void {
    /**
     * This unloads the page content before changing the step. If this is not done then there
     * is many JS errors as the cartOrOrder$ observable emits undefined before the page is
     * unloaded.
     */
    this.isChangingStep = true;
    this.cdr.detectChanges();
    this.bookingService.setStep({ step: BOOKING_STEPS.ENTRY });
  }

  addEmail(): void {
    const addedEmail = this.paxForm.get('additionalEmail').value;
    this.emailsArray.push(addedEmail);
    this.store$.dispatch(PaxDetailsActions.setAdditionalEmails({ additionalEmails: this.emailsArray }));
    const passengerFormDetails: PaxDetailsState = snapshot(this.paxDetails$);

    this.bookingQueueService.queuePassengersAndCorporateCodeUpdate(passengerFormDetails);

    this.paxForm.get('additionalEmail').reset();

    this.gtmService.trackElement(
      'additional-email',
      'booking-flow_purchase-review',
      ElementTypes.BUTTON,
      '',
      ElementActions.CLICK
    );
  }

  removeEmail(index: number): void {
    this.emailsArray.splice(index, 1);
    this.store$.dispatch(PaxDetailsActions.setAdditionalEmails({ additionalEmails: this.emailsArray }));
    const passengerFormDetails: PaxDetailsState = snapshot(this.paxDetails$);

    this.bookingQueueService.queuePassengersAndCorporateCodeUpdate(passengerFormDetails);
  }

  handleFullTicketRuleModalOpen(): void {
    this.isTicketRulesModalOpen = true;

    this.gtmService.trackElement(
      'full-ticket-rules::open-modal',
      GaContext.BOOKING_FLOW,
      ElementTypes.MODAL,
      undefined,
      ElementActions.CLICK
    );
  }

  handleFlightCardToggle(index: number): void {
    this.flightCardExpandersOpen[index] = !this.flightCardExpandersOpen[index];
  }

  handleViewDetailsModal(index: number): void {
    this.timelineBoundIndex = index;
    this.isTimelineModalOpen = true;
  }

  openTicketRulesModal(bound: FinnairBoundItem): void {
    this.isTicketRulesModalOpen = true;

    this.activeBoundInModal$.next(bound);
  }
}
